// The view-`focus` channel: the figure/ground split a `focus` clause makes,
// read by the board to VEIL the ground without touching layout.
//
// Shaped exactly like `useNodeColoring` / `useLifecyclePaint` — a paint context,
// deliberately absent from `useBoardLayout`'s input key, so switching the focus
// set repaints and never re-solves.
//
// Focus is NOT a member-highlight input. It used to be the last term of the
// `??` chain that also carries legend hover, aspect pin, type-instance select
// and blast radius, which meant a momentary gesture ERASED a standing property
// of the view (and that emphasis shared one yellow ring with "the neighbourhood
// of what I clicked"). Occlusion is its own channel: the gestures keep the
// ring/glow, focus takes the veil, and the two layer.

import { createContext, useContext } from "react";
import type { BoardItem, FocusRole } from "@archlang/scene/board";
import { isFigureKey } from "@archlang/scene/board";

/**
 * The live board's figure/ground resolver — the peer of the headless
 * `applyFocus` fold, over the same two rules (occlude the ground; figure is
 * inherited upward). Live cannot use the fold itself: folding would produce a
 * new `BoardSpace` on every focus change and push a layout-shaped object
 * through the render path, where all that changed is paint.
 */
export interface BoardFocus {
  /** Board keys (id or `logicalId`) the active view marks as figure. */
  readonly figure: ReadonlySet<string>;
  /** This item's role, with `figure` inherited up from its descendants. */
  role(item: BoardItem): FocusRole;
}

export const BoardFocusContext = createContext<BoardFocus | null>(null);

/** Null when no view focuses anything — the overwhelmingly common case, and the
 *  one where every consumer must cost exactly nothing. */
export function useBoardFocus(): BoardFocus | null {
  return useContext(BoardFocusContext);
}

/**
 * True inside the subtree of an item that is already veiled.
 *
 * ONE veil per ground subtree. A ground container's veil is drawn over its
 * whole item — nested space included — so a ground descendant that drew its own
 * would be occluded twice: 0.55 over 0.55 leaves 20% of the node, and recession
 * becomes deletion (§2.2). The headless fold gets this for free by not
 * descending into a ground container; live resolves each item independently and
 * needs to be told.
 */
export const GroundSubtreeContext = createContext(false);

export function useInsideGround(): boolean {
  return useContext(GroundSubtreeContext);
}

/**
 * Build a resolver over a figure key set. Returns null for an empty set so the
 * provider hands down `null` and the board's own render path short-circuits.
 *
 * The subtree walk is cached in a `WeakMap` keyed by the item object: board
 * items are stable across paint-only re-renders (they come out of the layout
 * memo), so a container is walked once per solve, not once per frame.
 */
export function makeBoardFocus(figure: ReadonlySet<string>): BoardFocus | null {
  if (figure.size === 0) return null;
  const cache = new WeakMap<BoardItem, FocusRole>();
  const role = (item: BoardItem): FocusRole => {
    const hit = cache.get(item);
    if (hit) return hit;
    // The SHARED rule (`@archlang/scene/board`), not a local re-derivation —
    // this resolver and the export fold are two implementations of one thing,
    // and the last time they each spelled the key themselves they disagreed:
    // the fold keyed through a helper that strips a leading `::`, so a grouped
    // member never matched and a focused export veiled its own figure.
    const own = isFigureKey(item, figure);
    // An item is figure if IT, or any descendant, is figure — so a container
    // never veils away the child the view is about, and a container whose whole
    // subtree is ground can be veiled with one rect.
    const r: FocusRole =
      own || item.space?.items.some((c) => role(c) === "figure") ? "figure" : "ground";
    cache.set(item, r);
    return r;
  };
  return { figure, role };
}
